import {
  InventoryOpsPoDetail,
  InventoryOpsReceivedSkuVariant,
  InventoryOpsRejectedSkuVariant,
  InventoryOpsSentSkuVariant,
  InventoryOpsSizeMix,
  InventoryOpsSkuVariantBase,
} from "../../../../../../models/ProjectManagement/ProjectInventoryProduct";

export interface InventoryOpsVariantTrackingRow {
  sku: string;
  title: string;
  orderedQty: number;
  rawInwardQty: number;
  rawInwardFirstAt: string | Date | null;
  rawInwardLastAt: string | Date | null;
  rawRejectedQty: number;
  rawQcFirstAt: string | Date | null;
  rawQcLastAt: string | Date | null;
  printOutwardQty: number;
  printOutwardFirstAt: string | Date | null;
  printOutwardLastAt: string | Date | null;
  printInwardQty: number;
  printInwardFirstAt: string | Date | null;
  printInwardLastAt: string | Date | null;
  printRejectedQty: number;
  printQcFirstAt: string | Date | null;
  printQcLastAt: string | Date | null;
}

const normalizeSku = (sku: string) => sku.trim().toLowerCase();

const toQty = (...values: Array<number | null | undefined>): number => {
  for (const value of values) {
    if (value != null && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return 0;
};

const toTimestamp = (value?: string | Date | null): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const pickEarlier = (
  a?: string | Date | null,
  b?: string | Date | null,
): string | Date | null => {
  const aTs = toTimestamp(a);
  const bTs = toTimestamp(b);
  if (aTs == null) return b ?? null;
  if (bTs == null) return a ?? null;
  return aTs <= bTs ? (a ?? null) : (b ?? null);
};

const pickLater = (
  a?: string | Date | null,
  b?: string | Date | null,
): string | Date | null => {
  const aTs = toTimestamp(a);
  const bTs = toTimestamp(b);
  if (aTs == null) return b ?? null;
  if (bTs == null) return a ?? null;
  return aTs >= bTs ? (a ?? null) : (b ?? null);
};

type VariantAgg = {
  qty: number;
  firstAt: string | Date | null;
  lastAt: string | Date | null;
};

const aggregateVariants = <T extends InventoryOpsSkuVariantBase>(
  variants: T[] | null | undefined,
  getQty: (variant: T) => number,
): Map<string, VariantAgg> => {
  const map = new Map<string, VariantAgg>();

  (variants || []).forEach((variant) => {
    if (!variant.sku) return;
    const sku = normalizeSku(variant.sku);
    const qty = getQty(variant);
    const created = variant.created_date ?? null;
    const modified = variant.modified_date ?? null;
    const firstAt = pickEarlier(created, modified);
    const lastAt = pickLater(modified, created);
    const existing = map.get(sku);

    if (!existing) {
      map.set(sku, { qty, firstAt, lastAt });
      return;
    }

    map.set(sku, {
      qty: Math.max(existing.qty, qty),
      firstAt: pickEarlier(existing.firstAt, firstAt),
      lastAt: pickLater(existing.lastAt, lastAt),
    });
  });

  return map;
};

/**
 * Build a per-variant tracking snapshot from size mixes + inventory ops details.
 * Used by the collapsible summary above Raw Inward.
 */
export const buildInventoryOpsVariantTrackingRows = (
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  po: InventoryOpsPoDetail | null | undefined,
): InventoryOpsVariantTrackingRow[] => {
  const rawInwardBySku = aggregateVariants(
    po?.raw_inward_sku_variants,
    (v: InventoryOpsReceivedSkuVariant) =>
      toQty(v.total_received_quantity, v.quantity_received),
  );
  const rawQcBySku = aggregateVariants(
    po?.raw_inward_qc_sku_variants,
    (v: InventoryOpsRejectedSkuVariant & { quantity?: number }) =>
      toQty(v.total_rejected_quantity, v.rejected_quantity, v.quantity),
  );
  const printOutBySku = aggregateVariants(
    po?.print_outward_sku_variants,
    (v: InventoryOpsSentSkuVariant) =>
      toQty(v.total_quantity_sent, v.quantity_sent),
  );
  const printInBySku = aggregateVariants(
    po?.print_inward_sku_variants,
    (v: InventoryOpsReceivedSkuVariant) =>
      toQty(v.total_received_quantity, v.quantity_received),
  );
  const printQcBySku = aggregateVariants(
    po?.print_inward_qc_sku_variants,
    (v: InventoryOpsRejectedSkuVariant & { quantity?: number }) =>
      toQty(v.total_rejected_quantity, v.rejected_quantity, v.quantity),
  );

  const titleBySku = new Map<string, string>();
  const orderedBySku = new Map<string, number>();

  (sizeMixes || []).forEach((mix) => {
    if (!mix.sku) return;
    const sku = normalizeSku(mix.sku);
    orderedBySku.set(sku, mix.quantity ?? 0);
    titleBySku.set(sku, mix.title?.trim() || mix.sku);
  });

  const allSkus = new Set<string>([
    ...Array.from(orderedBySku.keys()),
    ...Array.from(rawInwardBySku.keys()),
    ...Array.from(rawQcBySku.keys()),
    ...Array.from(printOutBySku.keys()),
    ...Array.from(printInBySku.keys()),
    ...Array.from(printQcBySku.keys()),
  ]);

  // Prefer size-mix order, then any extra SKUs from ops arrays.
  const orderedSkus = [
    ...(sizeMixes || [])
      .filter((mix) => Boolean(mix.sku))
      .map((mix) => normalizeSku(mix.sku)),
    ...Array.from(allSkus).filter(
      (sku) => !(sizeMixes || []).some((mix) => normalizeSku(mix.sku) === sku),
    ),
  ];

  const uniqueSkus = Array.from(new Set(orderedSkus));

  return uniqueSkus.map((sku) => {
    const rawInward = rawInwardBySku.get(sku);
    const rawQc = rawQcBySku.get(sku);
    const printOut = printOutBySku.get(sku);
    const printIn = printInBySku.get(sku);
    const printQc = printQcBySku.get(sku);
    const displaySku =
      (sizeMixes || []).find((mix) => normalizeSku(mix.sku) === sku)?.sku ||
      sku;

    return {
      sku: displaySku,
      title: titleBySku.get(sku) || displaySku,
      orderedQty: orderedBySku.get(sku) ?? 0,
      rawInwardQty: rawInward?.qty ?? 0,
      rawInwardFirstAt: rawInward?.firstAt ?? null,
      rawInwardLastAt: rawInward?.lastAt ?? null,
      rawRejectedQty: rawQc?.qty ?? 0,
      rawQcFirstAt: rawQc?.firstAt ?? null,
      rawQcLastAt: rawQc?.lastAt ?? null,
      printOutwardQty: printOut?.qty ?? 0,
      printOutwardFirstAt: printOut?.firstAt ?? null,
      printOutwardLastAt: printOut?.lastAt ?? null,
      printInwardQty: printIn?.qty ?? 0,
      printInwardFirstAt: printIn?.firstAt ?? null,
      printInwardLastAt: printIn?.lastAt ?? null,
      printRejectedQty: printQc?.qty ?? 0,
      printQcFirstAt: printQc?.firstAt ?? null,
      printQcLastAt: printQc?.lastAt ?? null,
    };
  });
};
